import { useMemo, useState } from "react";
import Core from "./clientSdk";
import {
  buildInvoiceDownloadFileName,
  downloadPdfBase64,
  extractInvoiceNumberFromPayout,
  getApiErrorMessage,
  pickInvoiceId,
} from "./payoutInvoiceUtils";

export function usePayoutInvoiceDownload({
  payoutData,
  payoutToken,
  assignmentName,
  assignmentId,
  onError,
  onInvoiceNumberSaved,
}) {
  const [downloading, setDownloading] = useState(false);
  const [downloadModalVisible, setDownloadModalVisible] = useState(false);
  const [customInvoiceId, setCustomInvoiceId] = useState("");
  const [autoGeneratedInvoiceId, setAutoGeneratedInvoiceId] = useState("");

  const payoutId = payoutData?.payout_id || null;
  const resolvedToken =
    payoutToken || payoutData?.payout_token || null;
  const existingInvoiceNumber = pickInvoiceId(payoutData?.invoice_number);
  const hasExistingInvoiceNumber = Boolean(existingInvoiceNumber);

  const fileName = useMemo(
    () =>
      buildInvoiceDownloadFileName(
        assignmentName || payoutData?.assignment_name || payoutData?.campaign_name,
        assignmentId || payoutData?.assignment_id || payoutId
      ),
    [
      assignmentName,
      assignmentId,
      payoutData?.assignment_name,
      payoutData?.campaign_name,
      payoutData?.assignment_id,
      payoutId,
    ]
  );

  function syncInvoiceNumberFromResponse(response, fallbackId) {
    const savedInvoiceId = extractInvoiceNumberFromPayout(
      payoutData,
      response
    );
    const nextInvoiceId = savedInvoiceId || pickInvoiceId(fallbackId);
    if (nextInvoiceId) {
      setAutoGeneratedInvoiceId(nextInvoiceId);
      onInvoiceNumberSaved?.(nextInvoiceId);
    }
  }

  function downloadInvoicePdf() {
    Core.generatePayoutInvoicePdf(
      (response) => {
        try {
          if (!response?.pdf_base64) {
            onError?.(
              "Invoice download is not available yet. Please try again later."
            );
            return;
          }
          downloadPdfBase64(response.pdf_base64, fileName);
        } catch (error) {
          onError?.(
            error?.message || "Unable to save invoice to your device."
          );
        } finally {
          setDownloading(false);
        }
      },
      (error) => {
        setDownloading(false);
        onError?.(
          getApiErrorMessage(error, "Unable to download invoice right now.")
        );
      },
      () => {},
      {
        payout_id: payoutId,
        token: resolvedToken,
      }
    );
  }

  function openDownloadModal() {
    if (downloading) {
      return;
    }
    if (!payoutId) {
      onError?.(
        "Payout ID is missing for this assignment. Please try again later."
      );
      return;
    }
    if (!resolvedToken) {
      onError?.(
        "Payout token is missing for this assignment. Please try again later."
      );
      return;
    }
    setCustomInvoiceId("");
    setDownloadModalVisible(true);
  }

  function closeDownloadModal() {
    if (downloading) {
      return;
    }
    setDownloadModalVisible(false);
    setCustomInvoiceId("");
  }

  function handleDownloadWithExisting() {
    setDownloadModalVisible(false);
    setCustomInvoiceId("");
    setDownloading(true);
    downloadInvoicePdf();
  }

  function handleDownloadWithAutoGenerated() {
    setDownloadModalVisible(false);
    setCustomInvoiceId("");
    setDownloading(true);

    Core.updatePayoutInvoiceNumber(
      (response) => {
        syncInvoiceNumberFromResponse(response);
        downloadInvoicePdf();
      },
      (error) => {
        setDownloading(false);
        onError?.(
          getApiErrorMessage(
            error,
            "Unable to generate invoice number right now."
          )
        );
      },
      () => {},
      {
        payout_id: payoutId,
        token: resolvedToken,
      }
    );
  }

  function handleDownloadWithCustom() {
    const customId = pickInvoiceId(customInvoiceId);
    if (!customId) {
      onError?.("Please enter an invoice number.");
      return;
    }
    if (hasExistingInvoiceNumber && customId === existingInvoiceNumber) {
      onError?.("Enter a different invoice number to update.");
      return;
    }

    setDownloadModalVisible(false);
    setDownloading(true);

    Core.updatePayoutInvoiceNumber(
      (response) => {
        syncInvoiceNumberFromResponse(response, customId);
        downloadInvoicePdf();
      },
      (error) => {
        setDownloading(false);
        onError?.(
          getApiErrorMessage(error, "Unable to save invoice number right now.")
        );
      },
      () => {},
      {
        payout_id: payoutId,
        invoice_id: customId,
        token: resolvedToken,
      }
    );
  }

  function initializeInvoiceNumberFromPayout(payout) {
    const generatedInvoiceId = pickInvoiceId(
      payout?.invoice_id,
      payout?.invoice_number
    );
    if (generatedInvoiceId) {
      setAutoGeneratedInvoiceId(generatedInvoiceId);
    }
  }

  return {
    downloading,
    downloadModalVisible,
    customInvoiceId,
    setCustomInvoiceId,
    autoGeneratedInvoiceId,
    existingInvoiceNumber,
    hasExistingInvoiceNumber,
    openDownloadModal,
    closeDownloadModal,
    handleDownloadWithExisting,
    handleDownloadWithAutoGenerated,
    handleDownloadWithCustom,
    initializeInvoiceNumberFromPayout,
  };
}
